import {
  Flex,
  NumberInputInputProps,
  NumberInputRootProps,
} from '@chakra-ui/react';
import React, { FunctionComponent as FC, useState } from 'react';
import { MdCheck } from 'react-icons/md';
import { additionRegex, hasAdditionRegex } from '../../constants/common';
import MCustomIconButton from './MCustomIconButton';
import { MCustomNumberInput } from './MCustomNumberInput';

/**
 * Lets zag's parser accept a leading "+" (it rejects any sign otherwise, so
 * the keystroke never lands). Only applied in addition mode: outside it, the
 * same `signDisplay` would turn a committed "15" into "+15" on blur.
 */
const ADDITION_FORMAT: Intl.NumberFormatOptions = {
  signDisplay: 'exceptZero',
  useGrouping: false,
};

interface MAdditionNumberInputProps extends Omit<
  NumberInputRootProps,
  'onChange'
> {
  styleProps?: NumberInputInputProps;
  quoteColors?: any;
  iconColor?: string;
  previousQuantity?: number | null;
  handleInputBlur: (add: boolean) => void;
  onChange?: (valueAsString: string, valueAsNumber: number) => void;
  placeholder?: string;
}

export const MAdditionNumberInput: FC<MAdditionNumberInputProps> =
  React.forwardRef<any, MAdditionNumberInputProps>(
    (
      {
        styleProps = {},
        quoteColors = {},
        iconColor,
        previousQuantity = 0,
        handleInputBlur,
        onChange,
        value,
        formatOptions,
        placeholder,
        ...rest
      }: MAdditionNumberInputProps,
      ref: any,
    ) => {
      const [additionValue, setAdditionValue] = useState<string>('+');
      const [additionMode, setAdditionMode] = useState<boolean>(false);

      const reset = () => {
        setAdditionMode(false);
        setAdditionValue('+');
      };

      // this will update the value
      const doAddition = () => {
        if (previousQuantity !== null) {
          let newValue = Number(value);
          const additionValueAsNumber = Number(additionValue);
          const prevAsNum = Number(previousQuantity);
          // if NaN, it is likely the onBlur value is just '+'. stop update/addition if so.
          if (!Number.isNaN(additionValueAsNumber)) {
            newValue = additionValueAsNumber + prevAsNum;
          }
          // only update if different
          if (onChange && String(newValue) !== value) {
            onChange && onChange(String(newValue), newValue);
          }
        }
        reset();
        handleInputBlur(additionMode);
      };

      // FIXME: breaking change
      // // trigger addition on return/enter key press
      // useHotkeys('return', () => additionMode && doAddition(), {
      //   enableOnTags: ['INPUT'],
      // });

      // new onChange for MCustomNumberInput does not update the actual value
      const handleAddition = (val: string) => {
        if (
          (hasAdditionRegex.test(String(val)) || additionMode) &&
          previousQuantity !== null
        ) {
          setAdditionMode(true);
          // Keep the "+" visible once digits follow it, as v2's input did.
          let newAdditionValue = additionRegex.test(val)
            ? `+${val.replace(/^\+/, '')}`
            : additionValue;
          // allow user to clear input
          if (val === '') {
            reset();
          }
          if (val === '+') {
            newAdditionValue = val;
          }
          // keep internalValue in sync
          setAdditionValue(newAdditionValue);
          return false;
        }
        // use internal handleChange of MCustomNumberInput
        return true;
      };

      // zag never lets a bare "+" reach `handleAddition` (the sign isn't valid
      // until addition mode switches `ADDITION_FORMAT` on), so the keystroke
      // itself starts addition mode.
      const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        styleProps.onKeyDown?.(event);
        if (event.key === '+' && !additionMode && previousQuantity !== null) {
          setAdditionMode(true);
          setAdditionValue('+');
        }
      };

      const getInput = () => (additionMode ? additionValue : value);
      const valueDiff = () =>
        additionMode && additionValue !== '+' && additionValue !== value;

      return (
        <Flex position="relative" width="100%">
          <MCustomNumberInput
            value={getInput()}
            handleChange={handleAddition}
            onChange={onChange}
            onBlur={doAddition}
            variant="quantity"
            styleProps={{ ...styleProps, onKeyDown: handleKeyDown }}
            width="100%"
            {...rest}
            formatOptions={additionMode ? ADDITION_FORMAT : formatOptions}
            // A bare "+" has no number to display, so show it as the hint.
            placeholder={
              additionMode && additionValue === '+' ? '+' : placeholder
            }
          />
          {valueDiff() && (
            <MCustomIconButton
              data-testid="addition-input-submit"
              position="absolute"
              left="100%"
              top="50%"
              transform="translateY(-50%)"
              ml={2}
              bg="none"
              btnSize={4}
              py={4}
              px={2}
              _hover={{
                background: 'none',
              }}
              iconColor={iconColor || 'tIndigo.base'}
              icon={MdCheck}
              aria-label="Apply"
              onClick={doAddition}
            />
          )}
        </Flex>
      );
    },
  );
